"use client";

import * as React from "react";
import { createPortal } from "react-dom";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  $createParagraphNode,
  $createTextNode,
  $getNodeByKey,
  $getSelection,
  $isRangeSelection,
  $isTextNode,
  $setSelection,
  $isParagraphNode,
  type LexicalNode,
  FORMAT_TEXT_COMMAND,
  BLUR_COMMAND,
  COMMAND_PRIORITY_LOW,
  type RangeSelection,
  TextNode,
} from "lexical";
import { $createHeadingNode, $isHeadingNode } from "@lexical/rich-text";
import { $isListItemNode, $isListNode, INSERT_ORDERED_LIST_COMMAND, INSERT_UNORDERED_LIST_COMMAND } from "@lexical/list";
import { TOGGLE_LINK_COMMAND, $isLinkNode, type LinkNode } from "@lexical/link";
import { $patchStyleText } from "@lexical/selection";
import {
  IconBold as Bold,
  IconItalic as Italic,
  IconUnderline as Underline,
  IconStrikethrough as Strikethrough,
  IconSuperscript as Superscript,
  IconSubscript as Subscript,
  IconLink as Link,
  IconLetterT as Type,
  IconH1 as Heading1,
  IconH2 as Heading2,
  IconH3 as Heading3,
  IconH4 as Heading4,
  IconH5 as Heading5,
  IconH6 as Heading6,
  IconListNumbers as ListOrdered,
  IconList as List,
  IconPaint as Paintbrush,
  IconChevronDown as ChevronDown,
} from "@tabler/icons-react";
import {
  Button,
  cn,
  Dropdown,
  Input,
  Label,
  Popover,
  Separator,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Toolbar,
  Tooltip,
} from "@heroui/react";
import type { Selection } from "react-aria-components";

import { getHeadingLevelLabel } from "../../kernel/constants";
import { findClosestBlockNodeKey } from "../../kernel/helpers/findClosestBlockNodeKey";

type BlockTypeValue =
  | "paragraph"
  | "heading-1"
  | "heading-2"
  | "heading-3"
  | "heading-4"
  | "heading-5"
  | "heading-6"
  | "ul"
  | "ol";

const BLOCK_TYPE_OPTIONS: Array<{
  type: BlockTypeValue;
  label: string;
  disabled?: boolean;
  Icon: React.ComponentType<{ className?: string }>;
}> = [
  { type: "paragraph", label: "段落", Icon: Type },
  { type: "heading-1", label: getHeadingLevelLabel(1), Icon: Heading1 },
  { type: "heading-2", label: getHeadingLevelLabel(2), Icon: Heading2 },
  { type: "heading-3", label: getHeadingLevelLabel(3), Icon: Heading3 },
  { type: "heading-4", label: getHeadingLevelLabel(4), Icon: Heading4 },
  { type: "heading-5", label: getHeadingLevelLabel(5), Icon: Heading5 },
  { type: "heading-6", label: getHeadingLevelLabel(6), Icon: Heading6 },
  { type: "ul", label: "无序列表", Icon: List },
  { type: "ol", label: "有序列表", Icon: ListOrdered },
];

const INLINE_FORMAT_IDS = ["bold", "italic", "underline", "strikethrough"] as const;

function selectionToStringSet(selection: Selection): Set<string> | null {
  if (selection === "all") return null;
  return new Set(Array.from(selection as Set<React.Key>).map(String));
}

const textColors = [
  { label: "红色", value: "#ef4444" },
  { label: "橙色", value: "#f97316" },
  { label: "黄色", value: "#eab308" },
  { label: "绿色", value: "#22c55e" },
  { label: "蓝色", value: "#3b82f6" },
  { label: "紫色", value: "#a855f7" },
  { label: "粉色", value: "#ec4899" },
  { label: "灰色", value: "#6b7280" },
  { label: "黑色", value: "#000000" },
];

const backgroundColors = [
  { label: "浅红", value: "#fee2e2" },
  { label: "浅橙", value: "#fed7aa" },
  { label: "浅黄", value: "#fef08a" },
  { label: "浅绿", value: "#dcfce7" },
  { label: "浅蓝", value: "#dbeafe" },
  { label: "浅紫", value: "#f3e8ff" },
  { label: "浅粉", value: "#fce7f3" },
  { label: "浅灰", value: "#f3f4f6" },
  { label: "深灰", value: "#e5e7eb" },
];

function CancelColorSwatch({
  active,
  onClick,
  title,
}: {
  active: boolean;
  onClick: () => void;
  title: string;
}) {
  return (
    <button
      type="button"
      className={cn(
        "relative h-7 w-7 overflow-hidden rounded-full border-2 border-divider bg-surface transition-colors",
        active && "border-primary ring-1 ring-primary",
      )}
      title={title}
      onMouseDown={(e) => {
        e.preventDefault();
      }}
      onClick={onClick}
    >
      <span
        aria-hidden="true"
        className="absolute left-1/2 top-1/2 h-[140%] w-[2px] bg-divider"
        style={{ transform: "translate(-50%, -50%) rotate(-45deg)" }}
      />
    </button>
  );
}

function normalizeHref(raw: string) {
  const href = raw.trim();
  if (!href) return "";
  if (/^https?:\/\//i.test(href)) return href;
  return `http://${href}`;
}

function parseStyleKV(style: string): Record<string, string> {
  const s = style ?? "";
  const out: Record<string, string> = {};
  for (const part of s.split(";")) {
    const p = part.trim();
    if (!p) continue;
    const idx = p.indexOf(":");
    if (idx < 0) continue;
    const k = p.slice(0, idx).trim().toLowerCase();
    const v = p.slice(idx + 1).trim();
    if (!k) continue;
    out[k] = v;
  }
  return out;
}

function getNearestLinkNode(node: LexicalNode | null | undefined): LinkNode | null {
  let cur: any = node ?? null;
  while (cur) {
    if ($isLinkNode(cur)) return cur as LinkNode;
    cur = cur.getParent?.() ?? null;
  }
  return null;
}

function getColorFromTextNodeStyle(style: string) {
  const kv = parseStyleKV(style);
  return { color: kv["color"] ?? "", backgroundColor: kv["background-color"] ?? "" };
}

export function TextToolbarPlugin({
  isEditable,
  containerRef,
}: {
  isEditable: boolean;
  containerRef: React.RefObject<HTMLDivElement | null>;
}) {
  const [editor] = useLexicalComposerContext();

  const keepVisibleRef = React.useRef(false);

  const [visible, setVisible] = React.useState(false);
  const [pos, setPos] = React.useState<{ top: number; left: number } | null>(null);
  const [activeBlockKey, setActiveBlockKey] = React.useState<string | null>(null);
  const [activeInListItem, setActiveInListItem] = React.useState(false);
  const [blockTypeValue, setBlockTypeValue] = React.useState<BlockTypeValue>("paragraph");

  const [formatActiveStates, setFormatActiveStates] = React.useState<string[]>([]);
  const [scriptActiveState, setScriptActiveState] = React.useState<"" | "superscript" | "subscript">("");

  const [isLink, setIsLink] = React.useState(false);
  const [currentLinkHref, setCurrentLinkHref] = React.useState("");

  const [linkPopoverOpen, setLinkPopoverOpen] = React.useState(false);
  const [linkUrl, setLinkUrl] = React.useState("");
  const [linkTextSnapshot, setLinkTextSnapshot] = React.useState("");
  const [ghostRects, setGhostRects] = React.useState<Array<{ left: number; top: number; width: number; height: number }>>([]);

  const latestLinkHrefRef = React.useRef<string>("");
  const latestLinkTextRef = React.useRef<string>("");

  const [colorPopoverOpen, setColorPopoverOpen] = React.useState(false);
  const [textColor, setTextColor] = React.useState("");
  const [backgroundColor, setBackgroundColor] = React.useState("");

  const savedRangeSelectionRef = React.useRef<RangeSelection | null>(null);
  const savedDomRangeRef = React.useRef<Range | null>(null);

  React.useEffect(() => {
    keepVisibleRef.current = linkPopoverOpen || colorPopoverOpen;
  }, [linkPopoverOpen, colorPopoverOpen]);

  const getBlockTypeByNode = React.useCallback((node: LexicalNode) => {
    if ($isParagraphNode(node)) return "paragraph";
    if ($isHeadingNode(node as any)) {
      const tag = (node as any).getTag?.();
      return `heading-${tag?.replace("h", "")}` as BlockTypeValue;
    }
    if ($isListItemNode(node as any)) {
      const parent = (node as any).getParent?.();
      if ($isListNode(parent)) {
        return parent.getTag() === "ul" ? "ul" : "ol";
      }
    }
    return "paragraph";
  }, []);

  const restoreSavedSelection = React.useCallback(() => {
    const saved = savedRangeSelectionRef.current;
    if (!saved) return;
    editor.update(() => {
      $setSelection(saved.clone());
    });
  }, [editor]);

  const applyInline = React.useCallback(
    (kind: "bold" | "italic" | "underline" | "strikethrough" | "superscript" | "subscript") => {
      editor.update(() => {
        const saved = savedRangeSelectionRef.current;
        if (!saved) return;
        $setSelection(saved.clone());
      });

      // 使用 Lexical 命令体系做格式化，避免 Selection 级别 API 在当前实现下不生效
      editor.dispatchCommand(FORMAT_TEXT_COMMAND, kind as any);
      editor.focus();
    },
    [editor],
  );

  const applyLink = React.useCallback(
    (url: string) => {
      editor.update(() => {
        const saved = savedRangeSelectionRef.current;
        if (!saved) return;
        $setSelection(saved.clone());
      });

      editor.dispatchCommand(
        TOGGLE_LINK_COMMAND,
        { url, target: "_blank", rel: "noopener noreferrer" } as any,
      );
      editor.focus();
    },
    [editor],
  );

  const applyColor = React.useCallback(
    (kind: "color" | "bgcolor", value: string) => {
      const prop: "color" | "background-color" = kind === "color" ? "color" : "background-color";
      editor.update(() => {
        const saved = savedRangeSelectionRef.current;
        if (!saved) return;
        $setSelection(saved.clone());

        const selection = $getSelection();
        if (!$isRangeSelection(selection)) return;
        if (selection.isCollapsed() || selection.getTextContent().trim().length === 0) return;

        // 对当前选区文本打样式；setStyle 只影响后续输入，不会稳定作用于已选文本
        $patchStyleText(selection, { [prop]: value || null });
      });
      editor.focus();
    },
    [editor],
  );

  const updateGhostRects = React.useCallback(() => {
    const r = savedDomRangeRef.current;
    if (!r) {
      setGhostRects([]);
      return;
    }
    try {
      const rects = Array.from(r.getClientRects())
        .map((rc) => ({
          left: rc.left,
          top: rc.top,
          width: rc.width,
          height: rc.height,
        }))
        .filter((rc) => rc.width > 0 && rc.height > 0);
      setGhostRects(rects);
    } catch {
      setGhostRects([]);
    }
  }, []);

  React.useEffect(() => {
    if (!isEditable) {
      setVisible(false);
      setActiveBlockKey(null);
      setLinkPopoverOpen(false);
      setColorPopoverOpen(false);
      return;
    }

    let raf = 0;
    const unregister = editor.registerUpdateListener(() => {
      if (raf) return;
      raf = window.requestAnimationFrame(() => {
        raf = 0;
        editor.getEditorState().read(() => {
          const selection = $getSelection();

          const container = containerRef.current;
          if (!container) {
            if (!keepVisibleRef.current) setVisible(false);
            return;
          }

          if (!$isRangeSelection(selection) || selection.isCollapsed() || selection.getTextContent().trim().length === 0) {
            if (!keepVisibleRef.current) setVisible(false);
            return;
          }

          const key = findClosestBlockNodeKey(selection.anchor.getNode());
          if (!key) {
            if (!keepVisibleRef.current) setVisible(false);
            return;
          }

          const el = editor.getElementByKey(key);
          if (!el) {
            if (!keepVisibleRef.current) setVisible(false);
            return;
          }

          const node = $getNodeByKey(key);
          if (!node) {
            if (!keepVisibleRef.current) setVisible(false);
            return;
          }

          const ok = $isParagraphNode(node) || $isHeadingNode(node as any) || $isListItemNode(node as any);
          if (!ok) {
            if (!keepVisibleRef.current) setVisible(false);
            return;
          }

          savedRangeSelectionRef.current = selection.clone();
          try {
            const domSel = window.getSelection();
            if (domSel && domSel.rangeCount > 0 && !domSel.isCollapsed) {
              savedDomRangeRef.current = domSel.getRangeAt(0).cloneRange();
            }
          } catch {
            // ignore
          }

          const r = el.getBoundingClientRect();
          // 工具条不得飘到容器顶部以上（容器上方是标题区等页面元素）
          const containerTop = containerRef.current?.getBoundingClientRect().top ?? 0;
          setPos({
            top: Math.max(Math.max(8, containerTop), r.top - 44),
            left: r.left,
          });

          setActiveBlockKey(key);
          setActiveInListItem($isListItemNode(node as any));
          const nextType = getBlockTypeByNode(node);
          setBlockTypeValue(nextType);

          const nextFormats: string[] = [];
          if (selection.hasFormat("bold")) nextFormats.push("bold");
          if (selection.hasFormat("italic")) nextFormats.push("italic");
          if (selection.hasFormat("underline")) nextFormats.push("underline");
          if (selection.hasFormat("strikethrough")) nextFormats.push("strikethrough");
          setFormatActiveStates(nextFormats);

          if (selection.hasFormat("superscript")) setScriptActiveState("superscript");
          else if (selection.hasFormat("subscript")) setScriptActiveState("subscript");
          else setScriptActiveState("");

          const linkNode = getNearestLinkNode(selection.anchor.getNode()) ?? getNearestLinkNode(selection.focus.getNode());
          if (linkNode) {
            setIsLink(true);
            setCurrentLinkHref(linkNode.getURL());
            latestLinkHrefRef.current = linkNode.getURL();
          } else {
            setIsLink(false);
            setCurrentLinkHref("");
            latestLinkHrefRef.current = "";
          }

          latestLinkTextRef.current = selection.getTextContent();

          const textNodes = selection.getNodes().filter((n): n is TextNode => $isTextNode(n));
          const extracted = textNodes.map((n) => getColorFromTextNodeStyle(n.getStyle()));
          const allColors = extracted.map((x) => x.color).filter(Boolean);
          const allBgs = extracted.map((x) => x.backgroundColor).filter(Boolean);

          const uniq = (arr: string[]) => Array.from(new Set(arr));
          setTextColor(allColors.length === 0 ? "" : uniq(allColors).length === 1 ? allColors[0] : "");
          setBackgroundColor(allBgs.length === 0 ? "" : uniq(allBgs).length === 1 ? allBgs[0] : "");

          setVisible(true);
        });
      });
    });

    return () => {
      if (raf) window.cancelAnimationFrame(raf);
      unregister();
    };
  }, [containerRef, editor, getBlockTypeByNode, isEditable]);

  React.useEffect(() => {
    if (!visible) return;
    const onSelectionChange = () => {
      try {
        const domSel = window.getSelection();
        if (domSel && domSel.rangeCount > 0 && !domSel.isCollapsed) {
          savedDomRangeRef.current = domSel.getRangeAt(0).cloneRange();
        }
      } catch {
        // ignore
      }
    };
    document.addEventListener("selectionchange", onSelectionChange);
    return () => document.removeEventListener("selectionchange", onSelectionChange);
  }, [visible]);

  /** 编辑器失焦时（如点击标题输入框）立即隐藏工具条，避免 fixed 浮层残留在标题区上方 */
  React.useEffect(() => {
    if (!isEditable) return;
    return editor.registerCommand(
      BLUR_COMMAND,
      () => {
        if (!keepVisibleRef.current) {
          setVisible(false);
        }
        return false;
      },
      COMMAND_PRIORITY_LOW,
    );
  }, [editor, isEditable]);

  React.useEffect(() => {
    if (!linkPopoverOpen) {
      setGhostRects([]);
      return;
    }
    updateGhostRects();
    const onResize = () => updateGhostRects();
    const onScroll = () => updateGhostRects();
    window.addEventListener("resize", onResize);
    document.addEventListener("scroll", onScroll, true);
    return () => {
      window.removeEventListener("resize", onResize);
      document.removeEventListener("scroll", onScroll, true);
    };
  }, [linkPopoverOpen, updateGhostRects]);

  const handleBlockTypeSelect = React.useCallback(
    (next: BlockTypeValue) => {
      const targetKey = activeBlockKey;
      if (!targetKey) return;

      restoreSavedSelection();

      editor.update(() => {
        const node = $getNodeByKey(targetKey);
        if (!node) return;

        const nextIsList = next === "ul" || next === "ol";

        // paragraph / heading
        if (!nextIsList) {
          const levelMatch = /^heading-(\d+)$/.exec(next);
          const toHeadingLevel = levelMatch ? Number(levelMatch[1]) : null;

          // heading <-> heading：直接替换为目标 heading，兼容不同 Lexical 版本对 setTag 的实现差异
          if ($isHeadingNode(node as any) && toHeadingLevel) {
            const heading = $createHeadingNode(`h${toHeadingLevel}` as any);
            const children = (node as any).getChildren?.() ?? [];
            if (children.length) heading.append(...children);
            else heading.append($createTextNode(node.getTextContent()));
            (node as any).replace?.(heading);
            (heading as any).selectStart?.();
            return;
          }

          const makeTarget = () => {
            if (toHeadingLevel) return $createHeadingNode(`h${toHeadingLevel}` as any);
            return $createParagraphNode();
          };

          const target = makeTarget();

          // list item -> paragraph/heading
          if ($isListItemNode(node as any)) {
            const maybePara =
              (node as any)
                .getChildren?.()
                .find((c: any) => $isParagraphNode(c)) ?? null;
            const inlineChildren = maybePara && $isParagraphNode(maybePara) ? (maybePara as any).getChildren?.() ?? [] : [];

            if (inlineChildren.length) target.append(...inlineChildren);
            else target.append($createTextNode(node.getTextContent()));

            (node as any).replace?.(target);
            (target as any).selectStart?.();
            return;
          }

          // paragraph / heading between：迁移 inline 子节点，保留行内样式
          const children = (node as any).getChildren?.() ?? [];
          if (children.length) target.append(...children);
          else target.append($createTextNode(node.getTextContent()));

          (node as any).replace?.(target);
          (target as any).selectStart?.();
          return;
        }

        // ul / ol: 依赖 selection + list plugin
        (node as any).selectStart?.();
      });

      if (next === "ul") editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
      if (next === "ol") editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
    },
    [activeBlockKey, editor, restoreSavedSelection],
  );

  const syncLinkFieldsFromSelection = React.useCallback(() => {
    editor.getEditorState().read(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        latestLinkTextRef.current = selection.getTextContent();
        const linkNode =
          getNearestLinkNode(selection.anchor.getNode()) ?? getNearestLinkNode(selection.focus.getNode());
        latestLinkHrefRef.current = linkNode?.getURL?.() ?? "";
      }
    });
    setLinkTextSnapshot(latestLinkTextRef.current || "（选中文字将显示在此）");
    setLinkUrl(latestLinkHrefRef.current || "");
  }, [editor]);

  const handleLinkPopoverChange = React.useCallback(
    (next: boolean) => {
      if (next) {
        syncLinkFieldsFromSelection();
        requestAnimationFrame(() => {
          restoreSavedSelection();
          editor.focus();
        });
      }
      setLinkPopoverOpen(next);
    },
    [editor, restoreSavedSelection, syncLinkFieldsFromSelection],
  );

  const handleColorPopoverChange = React.useCallback(
    (next: boolean) => {
      setColorPopoverOpen(next);
      if (next) requestAnimationFrame(() => editor.focus());
    },
    [editor],
  );

  const onBlockTypeMenuAction = React.useCallback(
    (key: React.Key) => {
      handleBlockTypeSelect(key as BlockTypeValue);
    },
    [handleBlockTypeSelect],
  );

  React.useEffect(() => {
    if (!linkPopoverOpen) return;
    editor.getEditorState().read(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        latestLinkTextRef.current = selection.getTextContent();
      }
    });
    setLinkTextSnapshot(latestLinkTextRef.current || "（选中文字将显示在此）");
  }, [editor, linkPopoverOpen]);

  const handleLinkConfirm = React.useCallback(() => {
    const href = normalizeHref(linkUrl);
    if (!href) return;
    setLinkPopoverOpen(false);
    setLinkUrl("");
    applyLink(href);
  }, [applyLink, linkUrl]);

  const handleTextColorSelect = React.useCallback(
    (color: string) => {
      setTextColor(color);
      applyColor("color", color);
    },
    [applyColor],
  );

  const handleBackgroundColorSelect = React.useCallback(
    (color: string) => {
      setBackgroundColor(color);
      applyColor("bgcolor", color);
    },
    [applyColor],
  );

  const handleFormatSelectionChange = React.useCallback(
    (selection: Selection) => {
      const nextSet = selectionToStringSet(selection);
      if (nextSet == null) return;
      for (const id of INLINE_FORMAT_IDS) {
        const was = formatActiveStates.includes(id);
        const now = nextSet.has(id);
        if (was !== now) applyInline(id);
      }
    },
    [applyInline, formatActiveStates],
  );

  const handleScriptSelectionChange = React.useCallback(
    (selection: Selection) => {
      const nextSet = selectionToStringSet(selection);
      if (nextSet == null) return;
      const next: "" | "superscript" | "subscript" = nextSet.has("superscript")
        ? "superscript"
        : nextSet.has("subscript")
          ? "subscript"
          : "";
      if (next === scriptActiveState) return;
      if (scriptActiveState) applyInline(scriptActiveState);
      if (next) applyInline(next);
    },
    [applyInline, scriptActiveState],
  );

  const shouldRender = isEditable && pos && activeBlockKey && (visible || linkPopoverOpen || colorPopoverOpen);
  const currentBlockOption = BLOCK_TYPE_OPTIONS.find((o) => o.type === blockTypeValue) ?? BLOCK_TYPE_OPTIONS[0];
  const BlockIcon = currentBlockOption.Icon;

  if (!shouldRender) return null;

  const toolbar = (
    <>
      {linkPopoverOpen &&
        ghostRects.length > 0 &&
        typeof document !== "undefined" &&
        createPortal(
          <div aria-hidden="true" className="pointer-events-none fixed inset-0 z-[1500]">
            {ghostRects.map((r, i) => (
              <div
                key={i}
                className="absolute rounded-[2px] bg-primary/20"
                style={{
                  left: `${r.left}px`,
                  top: `${r.top}px`,
                  width: `${r.width}px`,
                  height: `${r.height}px`,
                }}
              />
            ))}
          </div>,
          document.body,
        )}
      <Toolbar
        isAttached
        aria-label="选区格式"
        data-floating-root="true"
        className={cn(
          "inline-toolbar fixed z-[1000] max-w-[min(100vw-16px,720px)] border border-divider bg-surface shadow-lg",
          "animate-in fade-in-0 zoom-in-95",
        )}
        style={{ left: `${pos!.left}px`, top: `${pos!.top}px` }}
      >
        <div
          className="flex flex-row flex-wrap items-center gap-1 p-1"
          onPointerDownCapture={(e) => {
            const target = e.target as HTMLElement | null;
            if (!target) return;
            // React 的 portal 事件系统会把 document.body 下所有元素的捕获事件路由到这里，
            // 必须先确认点击发生在工具条自身的 DOM 内（具有 data-floating-root 标记的祖先），
            // 否则直接放行，避免阻止外部元素（如标题 textarea 及其 wrapper）获得焦点。
            // 不能依赖 e.currentTarget.contains()：portal 场景下 currentTarget 可能是
            // document.body，导致所有元素都被误判为"在工具条内"。
            if (!target.closest('[data-floating-root="true"]')) return;
            if (
              target.closest(
                '[data-slot="dropdown-menu"], [data-slot="dropdown-trigger"], [data-slot="dropdown-submenu-trigger"]',
              ) ||
              target.closest(
                '[data-slot="popover-root"], [data-slot="popover-trigger"], [data-slot="popover-dialog"], [data-slot="popover-content"]',
              ) ||
              target.closest('[data-slot="tooltip-root"], [data-slot="tooltip-trigger"], [role="tooltip"]') ||
              target.closest('[data-slot="dialog-content"], [data-slot="dialog-overlay"], [data-image-crop-dialog="true"]')
            ) {
              return;
            }
            if (target.closest('input, textarea, select, [contenteditable="true"], [role="textbox"]')) {
              return;
            }
            e.preventDefault();
          }}
        >
          <Dropdown>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 min-h-8 gap-1 px-2 font-medium"
              onMouseDown={(e) => e.preventDefault()}
            >
              <BlockIcon className="h-4 w-4 shrink-0" />
              <span>{currentBlockOption.label}</span>
              <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
            </Button>
            <Dropdown.Popover className="w-64" placement="bottom start">
              <Dropdown.Menu onAction={onBlockTypeMenuAction}>
                {BLOCK_TYPE_OPTIONS.map((opt) => {
                  const isHeading = opt.type.startsWith("heading-");
                  const disabledByContext = opt.disabled || (activeInListItem && isHeading);
                  return (
                    <Dropdown.Item
                      key={opt.type}
                      id={opt.type}
                      textValue={opt.label}
                      isDisabled={disabledByContext}
                      className={cn(blockTypeValue === opt.type && !disabledByContext && "bg-default-100")}
                    >
                      <opt.Icon className="h-4 w-4" />
                      <Label>{opt.label}</Label>
                    </Dropdown.Item>
                  );
                })}
              </Dropdown.Menu>
            </Dropdown.Popover>
          </Dropdown>

          <Separator orientation="vertical" className="!h-6 shrink-0" />

          <ToggleButtonGroup
            selectionMode="multiple"
            selectedKeys={new Set(formatActiveStates)}
            onSelectionChange={handleFormatSelectionChange}
            size="sm"
            className="shrink-0"
            aria-label="字符样式"
          >
            <ToggleButton id="bold" isIconOnly aria-label="加粗" onMouseDown={(e) => e.preventDefault()}>
              <Bold className="h-4 w-4" />
            </ToggleButton>
            <ToggleButton id="italic" isIconOnly aria-label="斜体" onMouseDown={(e) => e.preventDefault()}>
              <ToggleButtonGroup.Separator />
              <Italic className="h-4 w-4" />
            </ToggleButton>
            <ToggleButton id="underline" isIconOnly aria-label="下划线" onMouseDown={(e) => e.preventDefault()}>
              <ToggleButtonGroup.Separator />
              <Underline className="h-4 w-4" />
            </ToggleButton>
            <ToggleButton id="strikethrough" isIconOnly aria-label="删除线" onMouseDown={(e) => e.preventDefault()}>
              <ToggleButtonGroup.Separator />
              <Strikethrough className="h-4 w-4" />
            </ToggleButton>
          </ToggleButtonGroup>

          <Separator orientation="vertical" className="!h-6 shrink-0" />

          <ToggleButtonGroup
            selectionMode="single"
            selectedKeys={scriptActiveState ? new Set([scriptActiveState]) : new Set()}
            onSelectionChange={handleScriptSelectionChange}
            size="sm"
            className="shrink-0"
            disallowEmptySelection={false}
            aria-label="上标与下标"
          >
            <ToggleButton id="superscript" isIconOnly aria-label="上标" onMouseDown={(e) => e.preventDefault()}>
              <Superscript className="h-4 w-4" />
            </ToggleButton>
            <ToggleButton id="subscript" isIconOnly aria-label="下标" onMouseDown={(e) => e.preventDefault()}>
              <ToggleButtonGroup.Separator />
              <Subscript className="h-4 w-4" />
            </ToggleButton>
          </ToggleButtonGroup>

          <Separator orientation="vertical" className="!h-6 shrink-0" />

          <Popover isOpen={linkPopoverOpen} onOpenChange={handleLinkPopoverChange}>
            <Tooltip>
              <Tooltip.Trigger>
                <Popover.Trigger>
                  <ToggleButton
                    size="sm"
                    isIconOnly
                    className="relative h-8 w-8 min-h-8 min-w-8"
                    isSelected={isLink || linkPopoverOpen}
                    aria-label="链接"
                    onMouseDown={(e) => e.preventDefault()}
                  >
                    <Link className="h-4 w-4" />
                  </ToggleButton>
                </Popover.Trigger>
              </Tooltip.Trigger>
              <Tooltip.Content placement="top" offset={6}>
                链接
              </Tooltip.Content>
            </Tooltip>

            <Popover.Content className="w-72 p-3" placement="bottom start" offset={6}>
              <Popover.Dialog>
                <div
                  data-floating-root="true"
                  className="grid gap-3"
                  onPointerDownCapture={(e) => {
                    const target = e.target as HTMLElement | null;
                    if (!target) return;
                    if (!target.closest('[data-floating-root="true"]')) return;
                    if (target.closest('input, textarea, select, [contenteditable="true"], [role="textbox"]')) {
                      return;
                    }
                    e.preventDefault();
                  }}
                >
                  <TextField>
                    <Label>链接地址</Label>
                    <Input
                      value={linkUrl}
                      onChange={(e) => setLinkUrl(e.target.value)}
                      placeholder="请输入链接地址"
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleLinkConfirm();
                        if (e.key === "Escape") setLinkPopoverOpen(false);
                      }}
                    />
                  </TextField>

                  <div>
                    <Label className="mb-1.5 block text-sm font-medium text-foreground">链接文字</Label>
                    <div className="rounded-md border border-divider bg-default-100/80 px-3 py-2 text-sm text-muted">
                      {linkTextSnapshot || "（选中文字将显示在此）"}
                    </div>
                  </div>

                  <div className="flex justify-end gap-2">
                    <Button type="button" variant="ghost" size="sm" onPress={() => setLinkPopoverOpen(false)}>
                      取消
                    </Button>
                    <Button type="button" variant="primary" size="sm" onPress={handleLinkConfirm}>
                      确定
                    </Button>
                  </div>
                </div>
              </Popover.Dialog>
            </Popover.Content>
          </Popover>

          <Separator orientation="vertical" className="!h-6 shrink-0" />

          <Popover isOpen={colorPopoverOpen} onOpenChange={handleColorPopoverChange}>
            <Tooltip>
              <Tooltip.Trigger>
                <Popover.Trigger>
                  <ToggleButton
                    size="sm"
                    isIconOnly
                    className="relative h-8 w-8 min-h-8 min-w-8"
                    isSelected={colorPopoverOpen || !!textColor || !!backgroundColor}
                    aria-label="颜色"
                    onMouseDown={(e) => e.preventDefault()}
                  >
                    <Paintbrush className="h-4 w-4" />
                    {textColor || backgroundColor ? (
                      <span
                        className="absolute bottom-0.5 right-0.5 h-2.5 w-2.5 rounded-full border border-divider"
                        style={{
                          background: backgroundColor || "transparent",
                          borderColor: textColor || "currentColor",
                        }}
                      />
                    ) : null}
                  </ToggleButton>
                </Popover.Trigger>
              </Tooltip.Trigger>
              <Tooltip.Content placement="top" offset={6}>
                文字与背景颜色
              </Tooltip.Content>
            </Tooltip>

            <Popover.Content className="w-56 p-3" placement="top" offset={6}>
              <Popover.Dialog>
                <div
                  data-floating-root="true"
                  className="grid gap-4"
                  onPointerDownCapture={(e) => {
                    const target = e.target as HTMLElement | null;
                    if (!target) return;
                    if (!target.closest('[data-floating-root="true"]')) return;
                    if (target.closest('input, textarea, select, [contenteditable="true"], [role="textbox"]')) {
                      return;
                    }
                    e.preventDefault();
                  }}
                >
                  <div>
                    <Label className="mb-1.5 block text-sm font-medium text-foreground">文字颜色</Label>
                    <div className="grid grid-cols-5 gap-1.5">
                      <CancelColorSwatch title="取消文字颜色" active={!textColor} onClick={() => handleTextColorSelect("")} />
                      {textColors.map((c) => (
                        <button
                          key={c.value}
                          type="button"
                          className={cn(
                            "h-7 w-7 rounded-full border-2 transition-colors",
                            textColor === c.value && "border-primary ring-1 ring-primary",
                          )}
                          style={{
                            backgroundColor: c.value || "transparent",
                            borderColor: c.value ? c.value : "var(--color-divider)",
                          }}
                          title={c.label}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => handleTextColorSelect(c.value)}
                        />
                      ))}
                    </div>
                  </div>

                  <div>
                    <Label className="mb-1.5 block text-sm font-medium text-foreground">背景颜色</Label>
                    <div className="grid grid-cols-5 gap-1.5">
                      <CancelColorSwatch title="取消背景颜色" active={!backgroundColor} onClick={() => handleBackgroundColorSelect("")} />
                      {backgroundColors.map((c) => (
                        <button
                          key={c.value}
                          type="button"
                          className={cn(
                            "h-7 w-7 rounded-full border-2 transition-colors",
                            backgroundColor === c.value && "border-primary ring-1 ring-primary",
                          )}
                          style={{
                            backgroundColor: c.value || "transparent",
                            borderColor: c.value ? c.value : "var(--color-divider)",
                          }}
                          title={c.label}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => handleBackgroundColorSelect(c.value)}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </Popover.Dialog>
            </Popover.Content>
          </Popover>
        </div>
      </Toolbar>
    </>
  );

  if (typeof document === "undefined") return toolbar;
  // 挂载到 #root 而非 document.body：React 在同一个根容器内用 fiber 树确定事件处理
  // 范围，工具条的 onPointerDownCapture 只会对属于工具条 fiber 子树的点击触发；
  // 若挂到 document.body，React 会在 body 上注册独立的捕获监听，对文档内所有元素的
  // pointer 事件都派发，导致点击标题等外部元素时也误触 e.preventDefault()。
  const portalTarget = document.getElementById("root") ?? document.body;
  return createPortal(toolbar, portalTarget);
}

